<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import SearchDropdown from '@/app/components/SearchDropdown/SearchDropdown.vue';
import type {
  ItemGroup,
  Item,
} from '@/app/components/SearchDropdown/SearchDropdown.vue';
import { DATA_QA } from './csv-field-select.constants';
import logger from '@/logger';

type Props = {
  fields: string[];
  label?: string;
  row?: number;
};

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  row: 0,
});

const log = computed(() => logger.getChild(`CsvFieldSelect:${props.row}`));
log.value.debug('Mounting');
onMounted(() => {
  log.value.debug('Mounted');
});

const model = defineModel<string | null>();

const value = ref<Item<string> | null>(null);
const filter = ref('');

const itemFactory = (field: string): Item<string> => ({
  key: field,
  title: field,
  ref: field,
  icon: 'google-sheets',
});

const groups = computed(() => {
  return [
    {
      items: props.fields.map(itemFactory).filter((item) => {
        return item.title.toLowerCase().includes(filter.value.toLowerCase());
      }),
    },
  ] as ItemGroup<string>[];
});

watch(
  [model],
  () => {
    if (value.value?.ref === model.value) return;

    log.value.debug('Updating value', model.value);
    value.value = model.value ? itemFactory(model.value) : null;
  },
  { immediate: true },
);

watch([value], () => {
  if (model.value === value.value?.ref) return;
  log.value.debug('Updating model', value.value);
  model.value = value.value?.ref ?? null;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <SearchDropdown
      v-model="value"
      :label="label"
      :messages="$ta('CSV_FIELD_SELECT_MESSAGES')"
      :list-aria-label="$t('CSV_FIELD_SELECT_ARIA_LABEL')"
      :groups="groups"
      :debounce="0"
      :required="true"
      :show-list-icons="false"
      :placeholder="$t('CSV_FIELD_SELECT_PLACEHOLDER')"
      @update:search="(value) => (filter = value)"
    />
  </div>
</template>
